import React, { useState } from 'react';

import DefaultIcon from 'icons/ic-check.svg';
import HiddenIcon from 'icons/ic-restricted-16.svg';
import VisibleIcon from 'icons/ic-visible-16.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import { DropdownIndicator } from 'components/application/CollectionSelect';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function IconSelection({ icon, iconAllocation, index }) {
  const initialState = iconAllocation?.attributes.selectionState || 'visible';

  const [selectedState, setSelectedState] = useState(initialState);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleChange = (selectedOption) => {
    setSelectedState(selectedOption.value);
  };

  const isPersisted = iconAllocation !== undefined;
  const isCreating = selectedState !== 'visible' && !isPersisted;
  const isUpdating = selectedState !== 'visible' && isPersisted;
  const isDeleting = selectedState === 'visible' && isPersisted;
  const hasIconChanged = selectedState !== initialState;

  const {
    attributes: { imageAssetPath, name },
    id: iconId,
  } = icon;

  const renderHiddenInputs = (() => {
    if (isCreating) {
      return (
        <>
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][icon_id]`}
            type='hidden'
            value={iconId}
          />
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][selection_state]`}
            type='hidden'
            value={selectedState}
          />
        </>
      );
    } else if (isUpdating) {
      return (
        <>
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][id]`}
            type='hidden'
            value={iconAllocation.id}
          />
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][selection_state]`}
            type='hidden'
            value={selectedState}
          />
        </>
      );
    } else if (isDeleting) {
      return (
        <>
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][id]`}
            type='hidden'
            value={iconAllocation.id}
          />
          <input
            name={`project_configuration[icon_allocations_attributes][${index}][_destroy]`}
            type='hidden'
            value='1'
          />
        </>
      );
    }

    return null;
  })();

  const options = [
    { label: i18nTShared('visible'), value: 'visible' },
    { label: i18nTShared('selected'), value: 'selected' },
    { label: i18nTShared('hidden'), value: 'hidden' },
  ];

  const selectedOption = options.find(
    (option) => option.value === selectedState,
  );

  return (
    <div className='tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-p-2'>
      <div className='tw-mt-2'>
        <img alt={name} height={75} src={imageAssetPath} width={75} />
      </div>
      <div className='tw-h-full tw-pb-2.5 tw-pt-2.5 tw-text-center tw-align-top'>
        {name}
      </div>
      <div className='tw-mb-2 tw-h-10 tw-w-full'>
        <Select
          className='collection-select__select-container'
          classNamePrefix='collection-select'
          components={{ SingleValue, Option, DropdownIndicator }}
          onChange={handleChange}
          options={options}
          value={selectedOption}
        />
      </div>
      {/* Render hidden inputs for create, update, or destroy only if there's been a change */}
      {hasIconChanged && renderHiddenInputs}
    </div>
  );
}

IconSelection.propTypes = {
  icon: resourceShape('icon').isRequired,
  iconAllocation: resourceShape('iconAllocation'), // This might be null/undefined if no allocation exists
  index: PropTypes.number.isRequired,
};

function OptionValue(props) {
  const icon = {
    selected: <DefaultIcon className='tw-flex-none' height={16} width={16} />,
    visible: <VisibleIcon className='tw-flex-none' height={16} width={16} />,
    hidden: <HiddenIcon className='tw-flex-none' height={16} width={16} />,
  }[props.data.value];

  return (
    <div className='tw-flex tw-items-center'>
      {icon}
      <span className='tw-ml-2 tw-truncate'>{props.children}</span>
    </div>
  );
}

function SingleValue(props) {
  return (
    <components.SingleValue {...props}>
      <OptionValue {...props} />
    </components.SingleValue>
  );
}

function Option(props) {
  return (
    <components.Option {...props}>
      <div className='collection-select__option_container'>
        <OptionValue {...props} />
      </div>
    </components.Option>
  );
}
